import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { Box, Checkbox, FormControl, FormControlLabel, FormHelperText, Stack, TextField, Typography } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { DateTime } from 'luxon'
import { FC, ReactNode, useCallback, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { CreateLocationPermitSource, CreatePermitRequest, Location, LocationPermit } from '../../../../api/models'
import { useCreatePermitByAgencyType, useDeletePermitByAgencyType, useUpdatePermitByAgencyType } from '../../../../api/apiQueries'
import { AgencyTypeValues } from '../../../../constants/AgencyType'
import { useSnackbar } from '../../../../hooks/useSnackbar'
import { fromDatePicker, toDatePicker } from '../../../../utils/dateUtils'
import {
  displayAgencyPermitRequirement,
  getStatusActionConfirmation,
  getStatusActionPrompt,
  hasStatusAction,
  isMissingPermit
} from '../../../../utils/permitUtils'
import { AppFormLink } from '../JurisdictionDetailsTab/AppFormLink'
import { OrdinanceLink } from '../JurisdictionDetailsTab/OrdinanceLink'
import { getGuardPolicy } from '../JurisdictionDetailsTab/jurisdictionUtils'
import { GridColumn } from './GridColumn'
import { PermitExpiredCellContents } from './PermitExpiredCellContents'

export type ActionFunctions = {
  cancel: () => void
  save: () => void
}

type PermitColumnEditorProps = {
  location: Location
  permit?: LocationPermit
  agencyType: AgencyTypeValues
  col: number
  emitFunctions: (fns: ActionFunctions) => void
  actionButtons?: ReactNode
  onOperationSuccess: () => void
}

type PermitFormData = {
  permitNumber: string
  expirationDate: DateTime | null
  noNumberGiven: boolean
  noExpirationGiven: boolean
  permitNoLongerNeeded: boolean
  clearStatusAction: boolean
}

export const PermitColumnEditor: FC<PermitColumnEditorProps> = ({
  location,
  agencyType,
  permit,
  col,
  emitFunctions,
  actionButtons,
  onOperationSuccess
}) => {
  const createPermitByAgencyTypeMutation = useCreatePermitByAgencyType()
  const updatePermitByAgencyTypeMutation = useUpdatePermitByAgencyType()
  const deletePermitByAgencyTypeMutation = useDeletePermitByAgencyType()
  const { toast } = useSnackbar()

  const [originalPermitNumber, setOriginalPermitNumber] = useState(permit?.permitNumber || '')
  const [originalExpirationDate, setOriginalExpirationDate] = useState<DateTime | null>(toDatePicker(permit?.expirationDate))

  const renewalIsRequired = location.jurisdiction?.agencyRules[agencyType]?.permitRenewal?.isRequired
  const permitIsEternalInJurisdiction = renewalIsRequired === undefined ? false : !renewalIsRequired

  const [formValues, setFormValues] = useState<PermitFormData>({
    permitNumber: permit?.permitNumber || '',
    expirationDate: permit?.expirationDate ? toDatePicker(permit?.expirationDate) : null,
    noNumberGiven: permit ? !permit?.permitNumber : false,
    noExpirationGiven: permit ? !permit?.expirationDate : permitIsEternalInJurisdiction,
    permitNoLongerNeeded: false,
    clearStatusAction: false
  })

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors }
  } = useForm<PermitFormData>({
    values: formValues
  })

  const updateFormValue = (field: keyof PermitFormData, value: unknown) => {
    setFormValues(prev => ({ ...prev, [field]: value }))
  }

  useEffect(() => {
    reset(formValues)
  }, [formValues, reset])

  const noNumberGiven = watch('noNumberGiven')
  const noExpirationGiven = watch('noExpirationGiven')
  const statusActionNeeded = hasStatusAction(location, 'fire') || hasStatusAction(location, 'police')

  useEffect(() => {
    if (noNumberGiven) {
      setValue('permitNumber', '')
    } else {
      setValue('permitNumber', originalPermitNumber)
    }
    if (noExpirationGiven) {
      setValue('expirationDate', null)
    } else {
      setValue('expirationDate', originalExpirationDate)
    }
  }, [noNumberGiven, noExpirationGiven, setValue, originalPermitNumber, originalExpirationDate])

  const onSubmit = useCallback(
    async (data: PermitFormData) => {
      try {
        if (data.permitNoLongerNeeded) {
          if (permit) {
            await deletePermitByAgencyTypeMutation.mutateAsync({
              locationId: location.id,
              agencyType
            })
            toast('Permit deleted.', { variant: 'success' })
          }
        } else {
          const request: CreatePermitRequest = {
            source: CreateLocationPermitSource.SimpliSafe,
            isNotNumbered: data.noNumberGiven,
            isEternal: data.noExpirationGiven,
            permitNumber: data.noNumberGiven ? undefined : data.permitNumber,
            expirationDate: fromDatePicker(data.expirationDate)
          }

          if (permit) {
            await updatePermitByAgencyTypeMutation.mutateAsync({
              locationId: location.id,
              agencyType,
              request
            })
            toast('Permit changes saved.', { variant: 'success' })
          } else {
            await createPermitByAgencyTypeMutation.mutateAsync({
              locationId: location.id,
              agencyType,
              request
            })
            toast('Permit added.', { variant: 'success' })
          }
        }
        reset()
      } finally {
        onOperationSuccess()
      }
    },
    [
      agencyType,
      createPermitByAgencyTypeMutation,
      deletePermitByAgencyTypeMutation,
      location.id,
      onOperationSuccess,
      permit,
      reset,
      toast,
      updatePermitByAgencyTypeMutation
    ]
  )

  const cancel = useCallback(() => {}, [])

  const save = useCallback(() => {
    handleSubmit(onSubmit)()
  }, [handleSubmit, onSubmit])

  useEffect(() => {
    emitFunctions({ cancel, save })
  }, [cancel, emitFunctions, save])

  return (
    <GridColumn col={col} shouldShowBackground={hasStatusAction(location, agencyType)}>
      <Box sx={{ padding: 'var(--spacing-s)', position: 'relative', width: '100%' }} data-nobackground>
        <Box sx={{ position: 'absolute', right: '100%' }}>
          <PermitExpiredCellContents agencyType={agencyType} location={location} />
        </Box>
        <Stack sx={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <Stack sx={{ alignItems: 'center', flexDirection: 'row', fontWeight: 500 }}>{agencyType.toUpperCase()}</Stack>
          {actionButtons && <Box>{actionButtons}</Box>}
        </Stack>
      </Box>

      {statusActionNeeded && (
        <Stack
          data-testid={`${agencyType}-permit-prompt`}
          direction="row"
          spacing={'var(--spacing-2xs)'}
          alignItems="center"
          data-noborder
          useFlexGap
          sx={{ padding: 'var(--spacing-m) var(--spacing-m) var(--spacing-l) 0', color: 'var(--text-error)' }}
        >
          {hasStatusAction(location, agencyType) && (
            <>
              <ErrorOutlineIcon />
              {getStatusActionPrompt(location, agencyType)}
            </>
          )}
        </Stack>
      )}

      <Box sx={{ padding: 'var(--spacing-s)' }} data-testid={`${agencyType}-permit-grid-number-editor`}>
        {hasStatusAction(location, agencyType) && !isMissingPermit(location, agencyType) && (
          <Stack sx={{ padding: '0 0 var(--spacing-s) 0' }}>
            <Controller
              name="clearStatusAction"
              control={control}
              rules={{ required: 'Please confirm that you have verified the information below' }}
              render={({ field, fieldState }) => (
                <FormControl error={fieldState.invalid}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        data-testid={`${agencyType}-verified-checkbox`}
                        {...field}
                        checked={field.value}
                        onChange={e => {
                          field.onChange(e.target.checked)
                          updateFormValue('clearStatusAction', e.target.checked)
                        }}
                      />
                    }
                    label={<Typography>{getStatusActionConfirmation(location, agencyType)}</Typography>}
                  />
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Stack>
        )}
        <TextField
          {...register('permitNumber', {
            required: !noNumberGiven ? 'Permit number is required' : false,
            maxLength: {
              value: 50,
              message: 'Permit number cannot exceed 50 characters'
            },
            validate: value => noNumberGiven || value.trim() !== '' || 'Please provide a permit number',
            onChange: e => {
              setOriginalPermitNumber(e.target.value)
            }
          })}
          slotProps={{
            // @ts-expect-error - data-testid is a valid prop, but isn't in the type definition
            input: { ['data-testid']: 'permit-number-input' }
          }}
          fullWidth
          label="Permit Number"
          disabled={noNumberGiven}
          error={!!errors.permitNumber}
          helperText={errors.permitNumber?.message}
        />
        <Controller
          name="noNumberGiven"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  {...field}
                  checked={field.value}
                  onChange={e => {
                    field.onChange(e.target.checked)
                    updateFormValue('noNumberGiven', e.target.checked)
                  }}
                />
              }
              label="No number given"
            />
          )}
        />
      </Box>

      <Box sx={{ padding: 'var(--spacing-s)', fontWeight: 300 }} data-testid={`${agencyType}-permit-grid-expiration-editor`}>
        <Controller
          name="expirationDate"
          control={control}
          rules={{
            validate: (value: DateTime | null) => {
              if (!noExpirationGiven && (!value || !value.isValid)) {
                return 'Expiration is required'
              }
              return true
            }
          }}
          render={({ field }) => (
            <DatePicker
              {...field}
              label="Expiration"
              slotProps={{
                textField: {
                  error: !!errors.expirationDate,
                  helperText: errors.expirationDate?.message,
                  fullWidth: true,
                  inputProps: { 'data-testid': 'expiration-date-picker' }
                }
              }}
              disabled={noExpirationGiven}
              onChange={date => {
                field.onChange(date)
                updateFormValue('expirationDate', date)
                setOriginalExpirationDate(date)
              }}
            />
          )}
        />
        <Controller
          name="noExpirationGiven"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  {...field}
                  checked={field.value}
                  onChange={e => {
                    field.onChange(e.target.checked)
                    updateFormValue('noExpirationGiven', e.target.checked)
                  }}
                  slotProps={{
                    // @ts-expect-error - data-testid is a valid prop, but isn't in the type definition
                    input: { ['data-testid']: 'no-expiration-given-checkbox' }
                  }}
                />
              }
              label="No expiration given"
            />
          )}
        />

        {permit && (
          <Box sx={{ padding: 'var(--spacing-s) 0  0 0 ', fontWeight: 300 }}>
            <Controller
              name="permitNoLongerNeeded"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={field.value}
                      onChange={e => {
                        field.onChange(e.target.checked)
                        updateFormValue('permitNoLongerNeeded', e.target.checked)
                      }}
                    />
                  }
                  label={<Typography data-testid={'delete-checkbox-label'}>This permit is no longer needed</Typography>}
                />
              )}
            />
          </Box>
        )}
      </Box>

      <Box sx={{ padding: 'var(--spacing-s)', fontWeight: 300 }}>
        {location.jurisdiction?.agencyRules[agencyType]?.permitRenewal?.cadence || 'N/A'}
      </Box>

      <Box sx={{ padding: 'var(--spacing-s)', fontWeight: 300 }}>{displayAgencyPermitRequirement(location, agencyType)}</Box>

      <Box sx={{ padding: 'var(--spacing-s)', fontWeight: 300 }}>
        <AppFormLink agencyType={agencyType} location={location} />
      </Box>

      <Box sx={{ padding: 'var(--spacing-s)', fontWeight: 300 }}>
        <OrdinanceLink agencyType={agencyType} location={location} />
      </Box>

      <Box sx={{ padding: 'var(--spacing-s)', fontWeight: 300 }}>
        {location.jurisdiction?.agencyRules[agencyType]?.verifiedResponseRequirements?.raw.description || 'N/A'}
      </Box>

      <Box sx={{ padding: 'var(--spacing-s)', fontWeight: 300 }}>
        {location.jurisdiction?.agencyRules[agencyType]?.multipleAlarmTriggersRequirements?.raw?.description || 'N/A'}
      </Box>

      <Box sx={{ padding: 'var(--spacing-s)', fontWeight: 300 }}>
        {location.jurisdiction?.agencyRules[agencyType]?.enhancedCallVerificationRequirements?.count
          ? `${location.jurisdiction?.agencyRules[agencyType]?.enhancedCallVerificationRequirements?.count} Calls Required`
          : 'None'}
      </Box>
      <Box sx={{ padding: 'var(--spacing-s)', fontWeight: 300 }} data-testid={`${agencyType}-permit_guard_service`}>
        {location.jurisdiction ? getGuardPolicy(location.jurisdiction) : 'N/A'}
      </Box>
      <Box sx={{ padding: 'var(--spacing-s)', fontWeight: 300 }}>
        {location.jurisdiction?.agencyRules[agencyType]?.permitAdditionalNotes || 'N/A'}
      </Box>
    </GridColumn>
  )
}
